/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import Select, { components, Props as SelectProps, GroupBase, StylesConfig } from 'react-select';
import colors from 'ui/styles/colors.module.scss';
import sizes from 'ui/styles/sizes.module.scss';
import fontWeight from 'ui/styles/font-weights.module.scss';
import { withTranslation, WithTranslation } from 'react-i18next';

const sizeBase = Number(sizes.base);

const customStyles = (error = false): StylesConfig<unknown, boolean, GroupBase<unknown>> => ({
  container: (styles, { selectProps }) => ({
    ...styles,
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    marginBottom: sizeBase / 4,
    marginTop: sizeBase / 4,
  }),
  control: (base, { selectProps }) => ({
    ...base,
    backgroundColor: 'white',
    borderColor: error ? colors.error : colors.border,
    borderRadius: 8,
    cursor: 'pointer',
    minHeight: 48,
    outline: 'none',
    zIndex: selectProps.menuIsOpen ? 999 : 2,

    '&:hover': {
      borderColor: error ? colors.error : colors.border,
    },
    '&:focus-within': {
      cursor: 'default',
    },
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: colors.secondary,
    '&:hover': {
      color: colors.secondary,
    },
  }),
  option: (styles, { selectProps, isDisabled }) => ({
    ...styles,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    zIndex: selectProps.menuIsOpen ? 999 : 2,
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    backgroundColor: 'gray',
    display: 'none',
  }),
  input: (styles) => ({
    ...styles,
  }),
  placeholder: (styles) => ({
    ...styles,
    color: colors.secondary,
    fontWeight: fontWeight.regular,
  }),
  singleValue: (styles) => ({
    ...styles,
    color: colors.primary,
    lineHeight: 1.2,
  }),
  multiValue: (styles) => ({
    ...styles,
    display: 'none',
  }),
  clearIndicator: (styles, { selectProps }) => ({
    ...styles,
    display: selectProps.isClearable ? styles.display : 'none',
  }),
  valueContainer: (styles) => ({
    ...styles,
    paddingLeft: 14,
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 100,
  }),
});

const customDarkStyles = (error = false): StylesConfig<unknown, boolean, GroupBase<unknown>> => ({
  container: (styles, { selectProps }) => ({
    ...styles,
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    marginBottom: sizeBase / 4,
    marginTop: sizeBase / 4,
  }),
  control: (base, { selectProps }) => ({
    ...base,
    backgroundColor: colors.background,
    borderColor: 'transparent',
    borderRadius: 8,
    minHeight: 48,
    outline: 'none',
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    cursor: 'pointer',

    '&:hover': {
      borderColor: 'transparent',
    },
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: colors.secondary,
    '&:hover': {
      color: colors.secondary,
    },
  }),
  option: (styles, { selectProps, isDisabled, isSelected }) => ({
    ...styles,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    backgroundColor: isSelected ? colors.primary : colors.background,
    color: isSelected ? colors.background : colors.primary,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.background,
    },
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  menuList: (styles) => ({
    ...styles,
    backgroundColor: colors.background,
  }),
  input: (styles) => ({
    ...styles,
    color: colors.primary,
  }),
  placeholder: (styles) => ({
    ...styles,
    color: colors.secondary,
    fontWeight: fontWeight.regular,
  }),
  singleValue: (styles) => ({
    ...styles,
    color: colors.primary,
    lineHeight: 1.2,
  }),
  multiValue: (styles) => ({
    ...styles,
    display: 'none',
  }),
  clearIndicator: (styles, { selectProps }) => ({
    ...styles,
    display: selectProps.isClearable ? styles.display : 'none',
  }),
  valueContainer: (styles) => ({
    ...styles,
    paddingLeft: 14,
  }),
});

type SelectInputProps = React.ComponentProps<typeof components.Input> & {
  selectProps: { 'data-cy'?: string };
};

const Input = (inputProps: SelectInputProps) => {
  const { selectProps, ...props } = inputProps;
  const InputComponent = components.Input as unknown as React.ComponentType<Record<string, unknown>>;
  return <InputComponent {...props} data-cy={selectProps['data-cy']} />;
};

interface SelectType {
  name: string;
  style: {
    errorText: {
      color: string;
    };
  };
}

interface MySelectProps extends Omit<SelectProps<unknown, boolean, GroupBase<unknown>>, 'styles'>, WithTranslation {
  core?: React.ComponentType<SelectProps<unknown, boolean, GroupBase<unknown>>>;
  error?: string | null;
  styles?:
    | StylesConfig<unknown, boolean, GroupBase<unknown>>
    | ((error: boolean) => StylesConfig<unknown, boolean, GroupBase<unknown>>)
    | null;
}

// The select is reused with a wide variety of option shapes and props across the app,
// so the public component keeps the permissive prop typing it had as untyped JS.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PublicSelectProps = Record<string, any>;

const createInput = (type: SelectType) => {
  class MySelect extends React.Component<MySelectProps> {
    static type = type;

    static defaultProps = {
      core: null,
      error: null,
      styles: null,
    };

    render() {
      const { error, core, styles, t, ...props } = this.props;
      const Component = core || Select;
      const componentStyles = Object.assign(
        {},
        type.name === 'dark' ? customDarkStyles(!!error) : customStyles(!!error),
        typeof styles === 'function' ? styles(!!error) : styles || {},
      );

      return (
        <Component
          {...props}
          styles={componentStyles}
          components={{ Input: Input as typeof components.Input }}
          noOptionsMessage={() => t('no_options')}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              text: colors.primary,
              primary25: colors.border,
              primary: colors.primary,
            },
          })}
        />
      );
    }
  }
  const component = withTranslation()(MySelect) as unknown as React.ComponentType<PublicSelectProps> & {
    type: SelectType;
  };
  component.type = type;
  return component;
};

const dark: SelectType = {
  name: 'dark',
  style: {
    errorText: {
      color: colors.white,
    },
  },
};

const light: SelectType = {
  name: 'light',
  style: {
    errorText: {
      color: colors.error,
    },
  },
};

export const LightSelect = createInput(light);
export const DarkSelect = createInput(dark);
